import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PersonaCopy } from '../data/personaCopy';
import { X, AlertTriangle, ArrowRight, Clock, Gift } from 'lucide-react';

interface ExitIntentPopupProps {
  isVisible: boolean;
  copy: PersonaCopy;
  onClose: () => void;
  onSubmit: () => void;
}

const ExitIntentPopup: React.FC<ExitIntentPopupProps> = ({ 
  isVisible, 
  copy, 
  onClose, 
  onSubmit 
}) => {
  // Close popup when Escape key is pressed
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden'; // Prevent background scrolling
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVisible, onClose]);

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/50 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Popup Content */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-2xl max-w-md w-full overflow-hidden border border-white/20"
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-5 right-5 w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors z-20 backdrop-blur-sm border border-white/30"
          >
            <X className="w-4 h-4 text-white" />
          </button>

          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-secondary-600 p-6 text-white text-center relative overflow-hidden">
            <div className="absolute inset-0 bg-black/10" />
            <div className="relative z-10">
              <div className="w-16 h-16 bg-white/20 rounded-full mx-auto mb-4 flex items-center justify-center backdrop-blur-sm">
                <AlertTriangle className="w-8 h-8" />
              </div>
              <h2 className="text-2xl font-bold mb-2">
                {copy.microcopy.exitIntent.title}
              </h2>
              <p className="text-white/90 text-sm leading-relaxed">
                {copy.microcopy.exitIntent.message}
              </p>
            </div>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Urgency Indicators */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <Clock className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-red-700">Limited Spots</p>
                {/* <p className="text-xs text-red-600">Early access closing soon</p> */}
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <Gift className="w-6 h-6 text-blue-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-blue-700">Early Access</p>
                {/* <p className="text-xs text-blue-600">Be among the first</p> */}
              </div>
            </div>

            {/* Benefits List */}
            <div className="space-y-3 mb-6">
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-sm text-gray-700">Get early access to Quellect</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-sm text-gray-700">Exclusive launch updates</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-2 h-2 bg-green-500 rounded-full flex-shrink-0" />
                <span className="text-sm text-gray-700">Priority customer support</span>
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={onSubmit}
              className="w-full btn-primary text-lg py-4 relative overflow-hidden group"
            >
              <span className="relative z-10 flex items-center justify-center space-x-2">
                <span>{copy.microcopy.exitIntent.cta}</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary-600 to-secondary-600"
                initial={{ x: '-100%' }}
                animate={{ x: '0%' }}
                transition={{ duration: 0.3 }}
              />
            </button>

            {/* Secondary CTA */}
            <button
              onClick={onClose}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 py-3 transition-colors"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
